import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ControlsByUserIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetControlsByUserQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC controls data to match the GraphQL query structure
 */
const mapTrpcControlsToGraphQL = (
  trpcData: ControlsByUserIdResponseRow[]
): GetControlsByUserQuery => {
  return {
    control: trpcData,
  };
};

export const useGetControlsByUserId = (userId: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.control.controlsByUserId.queryOptions({ userId: userId }),

    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcControlsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
    error: trpcError,
  };
};
